/**
 * useSessionValidation — Boot-time session validity probe.
 *
 * On the first render after mount, if the auth store says `isAuthenticated`,
 * fire one `GET /customer/auth/profile`. Branch on response status:
 *  - 200          → update cached user (paranoid: in case server-side fields changed)
 *  - 404 / 410    → server confirms the user is gone. Purge the session.
 *  - 401          → no-op. The axios 401 interceptor already attempted a refresh
 *                    and called logout() itself if the refresh failed.
 *  - 5xx / netw   → no-op. Don't punish users for transient server/connectivity
 *                    issues. The next authenticated nav will trigger a fresh probe
 *                    via real API calls.
 *
 * Ref-guarded so it never re-fires within a single App mount (the validation is
 * only meaningful at boot; once we've ack'd the session is real, the rest of
 * the session lifetime is handled by the existing axios refresh queue).
 *
 * See: docs/superpowers/plans/2026-05-24-stork-rename-and-session-validation.md
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getProfile } from '@/api/auth';
import { isApiError } from '@/lib/errors';
import { useAuthStore } from '@/stores/useAuthStore';

export function useSessionValidation(): void {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const queryClient = useQueryClient();
    const validated = useRef(false);

    useEffect(() => {
        if (!isAuthenticated || validated.current) return;
        validated.current = true;

        getProfile()
            .then((user) => {
                useAuthStore.getState().updateUser(user);
            })
            .catch((err: unknown) => {
                // The axios response interceptor (app/lib/axios.ts) wraps every
                // rejection into an `ApiError` via `createApiError()`. There is
                // no `.response` property in production — read `.status` via
                // the `isApiError` type guard.
                const status = isApiError(err) ? err.status : undefined;
                if (status === 404 || status === 410) {
                    useAuthStore.getState().logout();
                    // TODO: consistency — useAuth.logoutMutation and this hook clear the query
                    // cache after logout, but the axios 401-refresh-fail logout path does not.
                    // Consider centralizing in useAuthStore.logout() so all paths behave the same.
                    queryClient.clear();
                    return;
                }
                // 401: axios interceptor handles it (refresh attempt → logout on fail).
                // 5xx / network (status 0) / undefined: leave cached state alone.
                // Warn on any unexpected client-status so a future API contract
                // change (e.g. backend starts returning 403 for a deleted user)
                // doesn't get silently absorbed by this hook.
                if (
                    status !== undefined &&
                    status !== 0 &&
                    status !== 401 &&
                    status < 500
                ) {
                    console.warn(
                        `[useSessionValidation] Unexpected status ${status} from getProfile; ignoring.`,
                    );
                }
            });
    }, [isAuthenticated, queryClient]);
}
